import "./active_applications.scss";
import { useEffect, useRef, useState } from "react";
import ApplicationCard from "../applications/application_card";
import useApplications from "@/hooks/use_applications";
import { Link, router } from "@inertiajs/react";
import EmptyState from "@components/empty_state.tsx";
import Fingerprint1 from "@images/fingerprint_1.svg";
import { useTranslation } from "react-i18next";
import MissingApplicationsModal from "@components/applications/missing_applications_modal.tsx";
import { SectionTitle } from "@/sprout/typography";
import ArrowRightIcon from "@/sprout/icons/arrow_right";
import { IconButton } from "@/sprout/icon_button";
import BackIcon from "@/sprout/icons/back";
import ForwardIcon from "@/sprout/icons/forward";
import { useSafePostHog } from "@hooks/use_posthog";

const ITEM_WIDTH = 304;
const ITEMS_PER_PAGE = 3;
const MAX_CAROUSEL_ITEMS = 9;

export const renderHeader = (count: number = 0) => {
  const { t } = useTranslation("applications");
  return (
    <Link href="/applications">
      <div className="active-applications__header">
        <SectionTitle> {t("active_applications_count", { count })}</SectionTitle>
        <div className="active-applications__header__icon">
          <ArrowRightIcon />
        </div>
      </div>
    </Link>
  );
};

const ActiveApplications = () => {
  const { t } = useTranslation("applications");
  const posthog = useSafePostHog();
  const { active } = useApplications({
    perPage: 10,
  });

  const applications = active.applications;
  const totalApplications = active.count;
  const carouselApplications = applications.slice(0, MAX_CAROUSEL_ITEMS);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [spacerWidth, setSpacerWidth] = useState(0);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);

  useEffect(() => {
    const updateSpacerWidth = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        setSpacerWidth(containerWidth - ITEM_WIDTH);
      }
    };

    const updateScrollState = () => {
      if (scrollerRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollerRef.current;
        const maxScrollLeft = scrollWidth - clientWidth;
        const displayWidth = clientWidth / ITEM_WIDTH;

        setIsAtStart(scrollLeft === 0);
        setIsAtEnd(scrollLeft >= maxScrollLeft - ITEM_WIDTH * displayWidth);
      }
    };

    const handleResize = () => {
      requestAnimationFrame(() => {
        updateSpacerWidth();
        updateScrollState();
      });
    };

    handleResize();

    const resizeObserver = new ResizeObserver(handleResize);

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    const handleWheelScroll = (event: WheelEvent) => {
      if (scrollerRef.current && isAtEnd && event.deltaX > 0) {
        event.preventDefault();
      }
    };

    const scroller = scrollerRef.current;
    if (scroller) {
      scroller.addEventListener("scroll", updateScrollState);
      scroller.addEventListener("wheel", handleWheelScroll, { passive: false });
    }

    return () => {
      resizeObserver.disconnect();
      if (scroller) {
        scroller.removeEventListener("scroll", updateScrollState);
        scroller.removeEventListener("wheel", handleWheelScroll);
      }
    };
  }, [carouselApplications, isAtEnd]);

  const handleApplicationSelect = (id: number) => {
    const application = applications.find((app) => app.id === id);
    posthog?.capture("card_clicked", {
      card_type: "application",
      source: "dashboard",
      application_id: id,
      job_title: application?.job_title,
      company_name: application?.company_name,
    });
    router.visit(`/applications/${id}`);
  };

  const scrollByPage = (direction: 1 | -1) => {
    if (!scrollerRef.current) return;

    const distance = ITEM_WIDTH * ITEMS_PER_PAGE;
    const { scrollLeft, scrollWidth, clientWidth } = scrollerRef.current;
    const maxScrollLeft = scrollWidth - clientWidth;

    scrollerRef.current.scrollBy({
      left:
        direction === 1
          ? Math.min(distance, maxScrollLeft - scrollLeft)
          : -Math.min(distance, scrollLeft),
      top: 0,
      behavior: "smooth",
    });
  };

  const emptyState = (
    <EmptyState
      svg={Fingerprint1}
      svgSize={"150"}
      title={t("find_great_job")}
      fontWeight={"400"}
      message={t("missing_an_application")}
      link={<MissingApplicationsModal triggerText={t("find_out_why")} />}
    />
  );

  return (
    <div ref={containerRef}>
      {renderHeader(totalApplications)}
      {!carouselApplications.length ? (
        emptyState
      ) : (
        <>
          <div className="active-applications__carousel-list flex gap-4" ref={scrollerRef}>
            {carouselApplications.map((application) => (
              <div className="active-applications__carousel-item" key={application.id}>
                <ApplicationCard
                  application={application}
                  selected={false}
                  onApplicationSelect={handleApplicationSelect}
                  expandTasksAsPopover
                />
              </div>
            ))}
            {/* Spacer element for extra scrollable space */}
            <div style={{ minWidth: spacerWidth }} />
          </div>
          {carouselApplications.length > ITEMS_PER_PAGE && (
            <div className="active-applications__carousel-buttons flex justify-end">
              <IconButton
                label="Scroll left"
                icon={BackIcon}
                size="md"
                onClick={() => scrollByPage(-1)}
                disabled={isAtStart}
              />
              <IconButton
                label="Scroll right"
                icon={ForwardIcon}
                size="md"
                onClick={() => scrollByPage(1)}
                disabled={isAtEnd}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ActiveApplications;
